import { Inertia } from '@inertiajs/inertia';
import NProgress from 'nprogress';
import debounce from 'lodash/debounce';

import { eventsBus } from '@/plugins/events';
import { $route } from '@/plugins/route';
import { getLogger } from '@/utils/logger';

const logger = getLogger('session');
const originalXMLHttpRequestSend = XMLHttpRequest.prototype.send;
const logoutUrl: string = $route('logout');
// The lowest value is `1` minute and we have to initiate session
// termination on the client a bit earlier so all the requests
// requiring authorization can be performed before the session
// is expired on the backend. So give it a `30` seconds window.
const lifetime: number = (window.appConfig.sessionLifetimeMins * 60_000) - 30_000;
let settled: true | undefined;
let timer: ReturnType<typeof setTimeout> | undefined;

XMLHttpRequest.prototype.send = function send(...args) {
  this.addEventListener('load', refreshSession);
  this.addEventListener('error', refreshSession);

  return originalXMLHttpRequestSend.apply(this, args);
}

function clearSessionTimeout(): void {
  if (timer) {
    clearTimeout(timer);
    timer = undefined;
  }
}

function settleSession(settle?: typeof settled): void {
  clearSessionTimeout();

  if (settle) {
    if (settled) {
      throw new Error('Invariant violation: configuring session timeout is not possible until logout.');
    }

    settled = settle;
  }

  if (settled) {
    logger('log', settle ? 'Settled' : 'Updated');
    timer = setTimeout(logout, lifetime);
  }
}

export const refreshSession = debounce(() => settleSession(), 1500);

export const setupSession = settleSession.bind(undefined, true);

/**
 * The function that guarantees a safe logout.
 */
export function logout(): Promise<void> {
  return new Promise((resolve) => {
    // Allow setting up the timer again.
    settled = undefined;

    const progress = NProgress.start();
    const logoutTasks: Array<Promise<unknown>> = [];

    eventsBus.logout.emit((task) => logoutTasks.push(task));

    Promise
      .all(logoutTasks.map((logoutTask) => {
        return logoutTask.then(() => {
          progress.inc();

          return logoutTask;
        });
      }))
      .then(() => {
        Inertia.visit(logoutUrl, {
          onFinish() {
            clearSessionTimeout();
            progress.done();
            logger('log', 'Halted');
            resolve();
          }
        });
      });
  });
}
